// frontend/src/utils/compressImage.ts
// Reduce una imagen (redimensionado + JPEG) antes de subirla, para que su base64
// quepa en el campo Dataverse `mserp_attachment` (máx 1.048.576 caracteres ≈ 786 KB
// de binario). El backend hace base64(file): base64 infla ~33%, así que apuntamos a
// un binario bajo ~750 KB con margen.
const MAX_DIM = 1600        // lado mayor máximo (px)
const MAX_BYTES = 740_000   // binario objetivo (≈ <1.048.576 chars en base64)

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/jpeg', quality))
}

/**
 * Devuelve un File JPEG redimensionado/comprimido por debajo de MAX_BYTES.
 * Si no es una imagen procesable, devuelve el original sin tocar.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  const bitmap = await createImageBitmap(file).catch(() => null)
  if (!bitmap) return file

  const baseScale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height))
  const toFile = (blob: Blob) =>
    new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })

  // Reintenta bajando calidad y, si no basta, reduciendo dimensiones.
  // Guarda el menor blob obtenido como fallback (siempre << original).
  let smallest: Blob | null = null
  for (const dimFactor of [1, 0.8, 0.6, 0.45]) {
    const scale = baseScale * dimFactor
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) break
    ctx.fillStyle = '#ffffff'           // JPEG sin alfa → fondo blanco
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(bitmap, 0, 0, w, h)

    for (const quality of [0.85, 0.75, 0.65, 0.55, 0.45]) {
      const blob = await canvasToBlob(canvas, quality)
      if (!blob) continue
      if (!smallest || blob.size < smallest.size) smallest = blob
      if (blob.size <= MAX_BYTES) { bitmap.close?.(); return toFile(blob) }
    }
  }
  bitmap.close?.()
  return smallest ? toFile(smallest) : file
}
